/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from "@hookform/resolvers/yup"
import { useDetailSalesOrder } from "pages/Sales/Transaction/CustomerOrder/utils/hooks"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"
import { useApi } from "utils"
import { today } from "utils/functions"
import { useDetailInvoice } from "../Invoice/utils/hooks/useDetailInvoice"
import { FormSection } from "./components"
import { formReturnItemsSchema, FormReturnItemsType } from "./types"
import { submitMap } from "./utils/functions"

export default function ReturnItems() {
  const navigate = useNavigate()
  const api = useApi()
  const params = useParams()
  const invoice = useDetailInvoice({
    invoice_id: params?.invoice_id ?? ""
  })
  const sales = useDetailSalesOrder({
    sales_order_id: invoice.data?.datainvoice.sales_order_id.toString() ?? ""
  })

  const defaultValues: FormReturnItemsType = useMemo(
    () => ({
      invoice_id: Number(params?.invoice_id) ?? NaN,
      trx_code: "",
      customer_id: sales.data?.dataso.customer_id ?? NaN,
      customer_name: "",
      location_id: invoice.data?.datainvoice.location_id ?? NaN,
      shipped: invoice.data?.datainvoice?.address ?? "",
      memo: "Return Item transaction",
      transaction_date: today(),
      referensi: "",
      approval_status_text: "",
      approved_by_name: "",
      so_payment_status: 1,
      so_payment_status_text: "",
      employee_id: sales.data?.dataso.employee_id ?? NaN,
      payment_term: sales.data?.dataso.payment_term ?? NaN,
      sales_order_id: sales.data?.dataso.sales_order_id ?? NaN,
      link: invoice.data?.datainvoice?.trx_code ?? "",
      delivery_no: invoice.data?.datainvoice.delivery_no ?? 1,
      promize_date: sales.data?.dataso?.promize_date ?? today(),
      tax_inclusive: true,
      payment_term_name: sales.data?.dataso?.payment_term_name ?? "",
      price_level_name: sales.data?.dataso?.price_level_name ?? "",
      return_detail: [],
      invoice_detail: invoice.data?.detailinvoice?.map(inv => {
        const qtyInvoice = inv.quantity_invoice ?? 0
        const qtyReturned = inv.quantity_returned ?? 0
        const qtyBackDelivered = qtyInvoice - qtyReturned

        return ({
          quantity_back_delivered: qtyBackDelivered,
          quantity_invoice: qtyInvoice,
          quantity_returned: qtyReturned,
          product_id: inv.product_id,
          product_name: inv.product_name,
          product_barcode: inv.product_barcode,
          mr_unit_id: inv.unit_convention_id,
          mr_unit_name: inv.unit_sell_measure_name,
          price_invoice: inv.price_invoice,
          discount: inv.discount,
          total: inv.total,
          tax_id: inv.tax_id,
          tax_name: `${inv.tax_name} (${inv.tax_rate}%)`,
          tax_rate: inv.tax_rate,
          quantity_unit: inv.quantity_unit,
          quantity_unit_sell_convention: inv.quantity_unit_sell_convention
        })
      }),
      freight: sales.data?.dataso?.freight ?? 0,
      approved_by: sales.data?.dataso?.approve_by ?? "",
      approval_status: 1,
      delivery_status: 1,
      imgPath: []
    }),
    [sales.data]
  )

  const submitPromise = async (value: FormReturnItemsType) => {
    return await toast
      .promise(api.post("/return/add", submitMap(value)), {
        loading: "Loading...",
        success: (res) => res.data.message,
        error: (err) =>
          err.response.data.detail?.message ?? err.response.data.message
      })
      .then(() => {
        navigate("/sales/register/", { replace: true })
      })
      .catch((err) => {
        throw new Error(err)
      })
  }

  const onSubmit = async (value: FormReturnItemsType) => {
    const hasInvalidQty = value.return_detail?.some((item) =>
      item.quantity_returned === undefined ||
      item.quantity_returned === null ||
      item.quantity_returned === 0
    )
  
    if (hasInvalidQty) {
      toast.error("Quantity Returned cannot be empty or 0")
      return
    }
    if (!value.return_detail || value.return_detail.length === 0) {
      toast.error("No items will be returned")
      return
    }

    return await submitPromise(value)
  }

  const methods = useForm<FormReturnItemsType>({
    defaultValues,
    resolver: yupResolver(formReturnItemsSchema)
  })

  useEffect(() => methods.reset(defaultValues), [defaultValues])

  return (
    <FormProvider {...methods}>
      <FormSection onSubmit={methods.handleSubmit(onSubmit)} />
    </FormProvider>
  )
}
